/**
 * Cross-tab refresh coordination for auth-v2 web clients.
 *
 * Each browser tab has its own JS heap, so the SDK's in-tab
 * single-flight guard is not enough: two tabs expiring at the same
 * time can both POST `/auth/refresh` with the same refresh `jti`.
 * The second call looks like reuse and revokes the whole session.
 *
 * One tab acquires a short localStorage lease, performs the refresh,
 * and broadcasts the result via BroadcastChannel. Follower tabs await
 * the broadcast instead of hitting `/auth/refresh` again.
 */

const CHANNEL_NAME = 'hero-auth-refresh';
const LOCK_KEY = 'hero.authRefreshLock';
const LOCK_TTL_MS = 15_000;
const TAB_ID =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `tab-${Math.random().toString(36).slice(2)}`;

type RefreshLock = { tabId: string; until: number };

export type RefreshBroadcastMessage = {
  type: 'refresh-done';
  accessToken: string | null;
};

function readLock(): RefreshLock | null {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as RefreshLock;
  } catch {
    return null;
  }
}

function tryBecomeLeader(): boolean {
  try {
    const now = Date.now();
    const existing = readLock();
    if (existing && existing.until > now && existing.tabId !== TAB_ID) {
      return false;
    }
    localStorage.setItem(LOCK_KEY, JSON.stringify({ tabId: TAB_ID, until: now + LOCK_TTL_MS }));
    const confirmed = readLock();
    return confirmed?.tabId === TAB_ID;
  } catch {
    return true;
  }
}

function releaseLockIfOwner(): void {
  try {
    const lock = readLock();
    if (lock?.tabId === TAB_ID) {
      localStorage.removeItem(LOCK_KEY);
    }
  } catch {
    /* ignore */
  }
}

function broadcastRefreshDone(accessToken: string | null): void {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage({ type: 'refresh-done', accessToken } satisfies RefreshBroadcastMessage);
    channel.close();
  } catch {
    /* ignore */
  }
}

function waitForPeerRefreshResult(timeoutMs: number): Promise<string | null | undefined> {
  if (typeof BroadcastChannel === 'undefined') {
    return Promise.resolve(undefined);
  }

  return new Promise(resolve => {
    let settled = false;
    const channel = new BroadcastChannel(CHANNEL_NAME);

    const finish = (value: string | null | undefined) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      channel.close();
      resolve(value);
    };

    const timer = setTimeout(() => finish(undefined), timeoutMs);

    channel.onmessage = (event: MessageEvent<RefreshBroadcastMessage>) => {
      if (event.data?.type === 'refresh-done') {
        finish(event.data.accessToken);
      }
    };
  });
}

/**
 * Wrap a refresh handler so only one tab performs the network call.
 * Other tabs wait for the winning tab to broadcast the new access token.
 */
export function withCrossTabRefreshLock(
  refreshHandler: () => Promise<string | null>,
): () => Promise<string | null> {
  return async (): Promise<string | null> => {
    if (typeof window === 'undefined') {
      return refreshHandler();
    }

    if (tryBecomeLeader()) {
      try {
        const token = await refreshHandler();
        broadcastRefreshDone(token);
        return token;
      } catch {
        broadcastRefreshDone(null);
        return null;
      } finally {
        releaseLockIfOwner();
      }
    }

    const peerToken = await waitForPeerRefreshResult(LOCK_TTL_MS);
    if (peerToken !== undefined) {
      return peerToken;
    }

    // Lock expired without a peer broadcast — safe to refresh ourselves.
    return refreshHandler();
  };
}
